import { useEffect } from "react";
import {
	SitePartSupplierFormContent,
	SitePartSupplierFormValues,
} from "src/components/views/erp/parts/siteParts/SitePartSupplierFormContent.tsx";
import { useAavoForm } from "src/components/common/forms/useAavoForm/useAavoForm.ts";
import { AavoFormLayout } from "src/components/common/forms/AavoFormLayout.tsx";
import { PartCreationWizardApi_FormInitData } from "src/api/generated/erp/parts/catalogPart/api/partCreationWizardApi.ts";
import { logError } from "src/errorHandling/errorLogging.ts";
import { PartCreationWizardState } from "src/components/views/erp/parts/catalogPart/partCreationWizard/types.ts";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox.tsx";
import i18n from "i18next";

interface SupplierPartFormStepProps {
	catalogPart: PartCreationWizardState["catalogPart"];
	supplierPart: PartCreationWizardState["supplierPart"];
	createSupplierPart: boolean;
	onUpdateCreateSupplierPart: (createSupplierPart: boolean) => void;
	onUpdateSupplierPart: (updates: PartCreationWizardState["supplierPart"]) => void;
	initData: PartCreationWizardApi_FormInitData;
	onValidationChange?: (isValid: boolean) => void;
}

export const SupplierPartFormStep = ({
	catalogPart,
	createSupplierPart,
	onUpdateCreateSupplierPart,
	supplierPart: currentSupplierPart,
	onUpdateSupplierPart,
	initData,
	onValidationChange,
}: SupplierPartFormStepProps) => {

	const defaultValues = {
		purchaseUnitId: catalogPart.partUnitId,
		purchaseFactor: 1,
		purchasePriceUnitId: catalogPart.partUnitId,
		purchasePriceFactor: 1,
		purchasePrice: 0,
		packSize: 1,
		isDefault: true,
		deliveryDuration: 1,
		...currentSupplierPart,
	}

	const useAavoFormReturn = useAavoForm<SitePartSupplierFormValues, void>({
		defaultValues: defaultValues,
		onSubmit: () => {},
	});
	const { watch, formState, trigger } = useAavoFormReturn;

	// Update values on first render
	useEffect(() => {
		onUpdateSupplierPart(defaultValues)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const subscription = watch((newValues) => {
			onUpdateSupplierPart(newValues);
		});
		return () => subscription.unsubscribe();
	}, [watch, onUpdateSupplierPart]);

	useEffect(() => {
		if (!formState.isValid) console.debug(formState.errors);
		onValidationChange?.(createSupplierPart ? formState.isValid : true);
	}, [formState.isValid, formState.errors, onValidationChange, createSupplierPart]);

	useEffect(() => {
		trigger().catch(logError);
	}, [trigger]);

	return (
		<AavoFormLayout columns={3}>
			<AavoCheckbox
				label={i18n.t("create_supplier_part")}
				checked={createSupplierPart}
				onChange={(checked) => onUpdateCreateSupplierPart(checked)}
				sx={{ gridColumn: "1/-1" }}
			/>
			{createSupplierPart && (
				<SitePartSupplierFormContent
					{...useAavoFormReturn}
					partId={null}
					supplierPartId={null}
					erpUnitOptions={initData.erpUnitOptions}
				/>
			)}
		</AavoFormLayout>
	);
};
